import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import { getInt } from 'utils/functions';
import FinancialProductSeasonalityTemplatesTable from './FinancialProductSeasonalityTemplatesTable';
import { PAYMENT_PLAN_TYPE_QUERY, FINANCIAL_PRODUCT_SEASONALITY_TEMPLATES_DICTIONARY_QUERY } from './queries';
import { ADD_UPDATE_FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE, REMOVE_FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE } from './mutations';
import {
  FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE_UPDATED,
  FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE_REMOVED,
  ALL_FINANCIAL_PRODUCT_SEASONALITY_TEMPLATES_REMOVED
} from './subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { get, set, useChangeTrackingLens, over } from '@totalsoft/change-tracking-react';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductSeasonalityTemplatesValidator } from './validators';
import { remove } from 'ramda';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductSeasonalityTemplatesContainer = ({ seasonalityTemplates, canWrite, isSaved, generalDataLens }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const productId = match.params.id && getInt(match.params.id);
  const [lockControls, setLockControls] = useState(false);
  const [seasonalityTemplatesLocal, setSeasonalityTemplatesLocal] = useState(seasonalityTemplates);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);

  useEffect(() => {
    setSeasonalityTemplatesLocal(seasonalityTemplates);
    resetLens(seasonalityTemplates);
  }, [resetLens, seasonalityTemplates]);

  useEffect(() => {
    resetLens(
      seasonalityTemplatesLocal.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, seasonalityTemplatesLocal]);

  const [financialProductSeasonalityTemplatesValidation, validateFinancialProductSeasonalityTemplates] = useDirtyFieldValidation(
    financialProductSeasonalityTemplatesValidator
  );

  useEffect(() => {
    validateFinancialProductSeasonalityTemplates(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProductSeasonalityTemplates]);

  const { data: paymentPlanType, loading: paymentPlanTypeLoading } = useQuery(PAYMENT_PLAN_TYPE_QUERY, {
    skip: !productId,
    variables: {
      productId
    }
  });

  const { data: financialProductSeasonalityTemplatesDictionary, loading: financialProductSeasonalityTemplatesDictionaryLoading } = useQuery(
    FINANCIAL_PRODUCT_SEASONALITY_TEMPLATES_DICTIONARY_QUERY,
    {
      skip: !paymentPlanType.financialProduct.leasingFinancialProduct.scheduleTypeId,
      variables: {
        paymentPlanType: paymentPlanType.financialProduct.leasingFinancialProduct.scheduleTypeId
      }
    }
  );

  const handleAdd = useCallback(() => {
    const currentList = [...(lens |> get)];
    currentList.push({
      seasonalityTemplateId: null,
      templateId: financialProductSeasonalityTemplatesDictionary?.financialProductSeasonalityTemplatesDictionary[0]?.templateId ?? null,
      default: false,
      isInEditing: true
    });
    set(lens)(currentList);
  }, [lens, financialProductSeasonalityTemplatesDictionary]);

  const handleEdit = useCallback(
    index => {
      const lensLocal = [...(lens |> get)];
      lensLocal[index].isInEditing = true;
      set(lens)(lensLocal);
    },
    [lens]
  );

  const handleCloseEdit = useCallback(
    (seasonalityTemplateId, index) => {
      if (!seasonalityTemplateId) set(lens)(remove(index, 1, lens |> get));
      else {
        const original = seasonalityTemplates.find(x => x.seasonalityTemplateId === seasonalityTemplateId);
        over(lens[index], prevValue => ({ ...prevValue, ...original, isInEditing: false }));
      }
    },
    [lens, seasonalityTemplates]
  );

  const handleChangeData = useCallback(
    (index, type, data) => {
      const lensLocal = [...(lens |> get)];
      if (type === 'default' && data === true) {
        lensLocal.forEach((element, indexList) => {
          if (indexList !== index) {
            element[type] = false;
          }
        });
      }
      lensLocal.forEach((element, indexList) => {
        if (indexList === index) {
          element[type] = data;
        }
      });
      set(lens)(lensLocal);
    },
    [lens]
  );

  const [saveFinancialProductSeasonalityTemplate] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE);

  const handleSave = useCallback(
    index => {
      const lensLocal = [...(lens |> get)];
      const elementTL = lensLocal[index];
      setLockControls(true);
      saveFinancialProductSeasonalityTemplate({
        variables: {
          input: {
            seasonalityTemplateId: parseInt(elementTL.seasonalityTemplateId),
            templateId: elementTL.templateId,
            default: elementTL.default,
            productId: productId,
            orderNo: index
          }
        }
      });
    },
    [lens, productId, saveFinancialProductSeasonalityTemplate]
  );

  useSubscription(FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.SeasonalityTemplates.FinancialProductSeasonalityTemplatesUpdated'), 'success');
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach((element, index) => {
        if (index === data.financialProductSeasonalityTemplateUpdated.orderNo) {
          element.seasonalityTemplateId = data.financialProductSeasonalityTemplateUpdated.seasonalityTemplate.seasonalityTemplateId;
          element.isInEditing = false;
        }
      });

      const hasSeasonalityTemplates = lensLocal.length > 0;

      client.cache.modify({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fields: {
          hasSeasonalityTemplates() {
            return hasSeasonalityTemplates;
          }
        }
      });

      set(generalDataLens.hasSeasonalityTemplates)(hasSeasonalityTemplates);
      setLockControls(false);
    }
  });

  const [deleteFinancialProductSeasonalityTemplate] = useMutation(REMOVE_FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE);

  const handleDelete = useCallback(
    (seasonalityTemplateId, index) => {
      const lensLocal = [...(lens |> get)];
      if (seasonalityTemplateId === undefined || seasonalityTemplateId === null) {
        lensLocal.splice(index, 1);
        set(lens)(lensLocal);
      } else {
        if (window.confirm(t('FinancialProduct.Details.SeasonalityTemplates.DeleteFinancialProductSeasonalityTemplates'))) {
          setLockControls(true);
          deleteFinancialProductSeasonalityTemplate({
            variables: {
              input: { seasonalityTemplateId }
            }
          });
        }
      }
    },
    [lens, t, deleteFinancialProductSeasonalityTemplate]
  );

  useSubscription(FINANCIAL_PRODUCT_SEASONALITY_TEMPLATE_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.SeasonalityTemplates.FinancialProductSeasonalityTemplatesRemoved'), 'success');
      over(lens, prevState =>
        prevState.filter(el => el.seasonalityTemplateId !== data.financialProductSeasonalityTemplateDeleted.seasonalityTemplateId)
      );
      client.cache.evict({
        id: `FinancialProductSeasonalityTemplateItem:{"seasonalityTemplateId":${data.financialProductSeasonalityTemplateDeleted.seasonalityTemplateId}}`
      });

      const lensLocal = [...(lens |> get)];
      const hasSeasonalityTemplates = lensLocal.length > 1;

      client.cache.modify({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fields: {
          hasSeasonalityTemplates() {
            return hasSeasonalityTemplates;
          }
        }
      });

      set(generalDataLens.hasSeasonalityTemplates)(hasSeasonalityTemplates);
      setLockControls(false);
    }
  });

  useSubscription(ALL_FINANCIAL_PRODUCT_SEASONALITY_TEMPLATES_REMOVED, {
    onData: ({ _, client }) => {
      addToast(t('FinancialProduct.Details.SeasonalityTemplates.AllFinancialProductSeasonalityTemplatesRemoved'), 'success');
      get(lens).forEach(financialProductSeasonalityTemplate =>
        client.cache.evict({
          id: `FinancialProductSeasonalityTemplateItem:{"seasonalityTemplateId":${financialProductSeasonalityTemplate.seasonalityTemplateId}}`
        })
      );

      client.cache.modify({
        id: `FinancialProductItem:{"productId":${productId}}`,
        fields: {
          hasSeasonalityTemplates() {
            return false;
          }
        }
      });

      set(generalDataLens.hasSeasonalityTemplates)(false);
      set(lens)([]);
      setLockControls(false);
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return !paymentPlanTypeLoading && paymentPlanType.financialProduct.leasingFinancialProduct.scheduleTypeId ? (
    !financialProductSeasonalityTemplatesDictionaryLoading &&
    financialProductSeasonalityTemplatesDictionary.financialProductSeasonalityTemplatesDictionary.length ? (
      <FinancialProductSeasonalityTemplatesTable
        canWrite={canWrite}
        seasonalityTemplates={lens |> get}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onChangeData={handleChangeData}
        onCloseEdit={handleCloseEdit}
        onSave={handleSave}
        onDelete={handleDelete}
        isSaved={isSaved}
        lockControls={lockControls}
        financialProductSeasonalityTemplatesDictionary={
          financialProductSeasonalityTemplatesDictionary?.financialProductSeasonalityTemplatesDictionary
        }
        financialProductSeasonalityTemplatesValidation={financialProductSeasonalityTemplatesValidation}
      />
    ) : (
      <>{t('FinancialProduct.Details.SeasonalityTemplates.NoSeasonalityTemplatesAvailable')}</>
    )
  ) : (
    <>{t('FinancialProduct.Details.SeasonalityTemplates.AddPaymentPlanType')}</>
  );
};

FinancialProductSeasonalityTemplatesContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  seasonalityTemplates: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired,
  generalDataLens: PropTypes.object.isRequired
};

export default FinancialProductSeasonalityTemplatesContainer;
